import "./i18n";
import React, { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
  Outlet,
} from "react-router-dom";
import { Layout, Menu, Spin, Tooltip, ConfigProvider, theme } from "antd";
import {
  DashboardOutlined,
  CarOutlined,
  AlertOutlined,
  ToolOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  MessageOutlined,
  BarChartOutlined,
  EllipsisOutlined,
  BugOutlined,
  ExperimentOutlined,
  ExperimentFilled,
  ThunderboltOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  DatabaseOutlined,
  FieldTimeOutlined,
} from "@ant-design/icons";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FactoryProvider, useFactory } from "./store/factory";
import { RoleProvider, useRole } from "./store/role";
import TopBar from "./components/TopBar";
import SidebarUserMenu from "./components/SidebarUserMenu";
import "./components/app-ui.css";

const Landing = lazy(() => import("./pages/Landing"));
const Solutions = lazy(() => import("./pages/Solutions"));
const Why = lazy(() => import("./pages/Why"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const CompanyAbout = lazy(() => import("./pages/CompanyAbout"));
const CompanyPress = lazy(() => import("./pages/CompanyPress"));
const Login = lazy(() => import("./pages/Login"));
const TruckMap = lazy(() => import("./pages/TruckMap"));
const DeliveryHistory = lazy(() => import("./pages/DeliveryHistory"));
const Savings = lazy(() => import("./pages/Savings"));
const Recommendations = lazy(() => import("./pages/Recommendations"));
const Chat = lazy(() => import("./pages/Chat"));
const Debug = lazy(() => import("./pages/Debug"));
const PilotRunner = lazy(() => import("./pages/PilotRunner"));
const Dashboards = lazy(() => import("./pages/Dashboards"));
const Home = lazy(() => import("./pages/Home"));
const MixerStatus = lazy(() => import("./pages/MixerStatus"));
const BaseMixes = lazy(() => import("./pages/BaseMixes"));
const RealtimeCorrections = lazy(() => import("./pages/RealtimeCorrections"));
const FieldAdditive = lazy(() => import("./pages/FieldAdditive"));
const ManagerOverview = lazy(() => import("./pages/ManagerOverview"));
const DriverTimes = lazy(() => import("./pages/DriverTimes"));
const ManagerSites = lazy(() => import("./pages/ManagerSites"));
const ManagerStations = lazy(() => import("./pages/ManagerStations"));
const ManagerMaterials = lazy(() => import("./pages/ManagerMaterials"));
const Tickets = lazy(() => import("./pages/Tickets"));
const Mixers = lazy(() => import("./pages/Mixers"));
const MixerDetail = lazy(() => import("./pages/MixerDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const Support = lazy(() => import("./pages/Support"));
const Recipes = lazy(() => import("./pages/Recipes"));
const Strength = lazy(() => import("./pages/Strength"));
const PilotSummary = lazy(() => import("./pages/PilotSummary"));
const FlowCommandHistory = lazy(() => import("./pages/FlowCommandHistory"));

const { Sider, Content, Header } = Layout;
const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000 } },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem("token");
  const location = useLocation();
  if (!token)
    return <Navigate to="/app/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

// Nav differs by demo role: a loader lives in the live plant/fleet; a
// technologist lives in recipes, strength data and monthly performance.
// `label` is an i18n key under appNav.*, resolved with t() at render time.
const LOADER_NAV = [
  { key: "/app/home", icon: <DashboardOutlined />, label: "appNav.plantControl" },
  { key: "/app/mixer-status", icon: <ToolOutlined />, label: "appNav.mixerStatus" },
  { key: "/app/manager/driver-times", icon: <FieldTimeOutlined />, label: "appNav.driverTimes" },
  { key: "/app/manager/sites", icon: <EnvironmentOutlined />, label: "appNav.managerSites" },
  { key: "/app/manager/stations", icon: <TeamOutlined />, label: "appNav.managerStations" },
  { key: "/app/map", icon: <CarOutlined />, label: "appNav.myFleet" },
  { key: "/app/flow-commands", icon: <ThunderboltOutlined />, label: "appNav.flowCommands" },
  { key: "/app/tickets", icon: <AlertOutlined />, label: "appNav.tickets" },
];
const TECHNOLOGIST_NAV = [
  { key: "/app/home", icon: <DashboardOutlined />, label: "appNav.mixPerformance" },
  { key: "/app/base-mixes", icon: <FileTextOutlined />, label: "appNav.baseMixes" },
  { key: "/app/realtime-corrections", icon: <ExperimentFilled />, label: "appNav.realtimeCorrections" },
  { key: "/app/field-additive", icon: <AlertOutlined />, label: "appNav.fieldAdditive" },
  { key: "/app/strength", icon: <BarChartOutlined />, label: "appNav.strength" },
  { key: "/app/pilot-summary", icon: <ExperimentOutlined />, label: "appNav.pilotSummary" },
];

// Checked less often than the primary nav — mirrors how Strength Results,
// Settings and Support surface in the mobile app (icon-opened, not a tab).
// Shown under both roles, regardless of which primary nav is active.
const SECONDARY_NAV = [
  { key: "/app/dashboards", icon: <BarChartOutlined />, label: "appNav.analytics" },
  { key: "/app/settings", icon: <SettingOutlined />, label: "appNav.settings" },
  { key: "/app/support", icon: <QuestionCircleOutlined />, label: "appNav.support" },
  { key: "/app/debug", icon: <BugOutlined />, label: "appNav.debug" },
];

// Manager screens — not tied to either demo role, shown under both.
// Overview and Raw Materials stay shared across both roles; Driver Times,
// Sites and Stations moved into the Control Manager's own primary nav above.
const MANAGER_NAV = [
  { key: "/app/manager/overview", icon: <DashboardOutlined />, label: "appNav.managerOverview" },
  { key: "/app/manager/materials", icon: <DatabaseOutlined />, label: "appNav.managerMaterials" },
];

// The small subset of PRIMARY_NAV shown as persistent bottom-tab icons on
// mobile; everything else (including all of SECONDARY_NAV) lives in "More".
const MOBILE_TABS = [
  { key: "/app/home", icon: <DashboardOutlined />, label: "Home" },
  { key: "/app/map", icon: <CarOutlined />, label: "Fleet" },
  { key: "/app/tickets", icon: <AlertOutlined />, label: "Tickets" },
];

const MOBILE_MORE = [
  { key: "/app/dashboards", icon: <BarChartOutlined />, label: "Analytics" },
  { key: "/app/mixers", icon: <ToolOutlined />, label: "Mixers" },
  { key: "/app/recipes", icon: <FileTextOutlined />, label: "Recipes" },
  { key: "/app/chat", icon: <MessageOutlined />, label: "Assistant" },
  { key: "/app/strength", icon: <BarChartOutlined />, label: "Strength Results" },
  { key: "/app/pilot-summary", icon: <ExperimentOutlined />, label: "Pilot Summary" },
  { key: "/app/settings", icon: <SettingOutlined />, label: "Settings" },
  { key: "/app/support", icon: <QuestionCircleOutlined />, label: "Support" },
  { key: "/app/debug", icon: <BugOutlined />, label: "Debug" },
];

function MobileTabBar() {
  const location = useLocation();
  const [moreOpen, setMoreOpen] = React.useState(false);
  const isActive = (key: string) => location.pathname === key || (key !== "/app" && location.pathname.startsWith(key));
  const moreActive = MOBILE_MORE.some((m) => isActive(m.key));

  return (
    <>
      {moreOpen && (
        <>
          <div
            onClick={() => setMoreOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 99 }}
          />
          <div className="mobile-more-sheet">
            {MOBILE_MORE.map((m) => (
              <Link key={m.key} to={m.key} className="mobile-more-item" onClick={() => setMoreOpen(false)}>
                {m.icon}
                {m.label}
              </Link>
            ))}
          </div>
        </>
      )}
      <nav className="mobile-tab-bar">
        {MOBILE_TABS.map((t) => (
          <Link key={t.key} to={t.key} className={`mobile-tab-btn${isActive(t.key) ? " active" : ""}`}>
            {t.icon}
            <span>{t.label}</span>
          </Link>
        ))}
        <button
          className={`mobile-tab-btn${moreActive ? " active" : ""}`}
          onClick={() => setMoreOpen((o) => !o)}
        >
          <EllipsisOutlined />
          <span>More</span>
        </button>
      </nav>
    </>
  );
}

function FleetLayout() {
  const { user } = useFactory();
  const { role } = useRole();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);

  const primaryNav = role === "technologist" ? TECHNOLOGIST_NAV : LOADER_NAV;

  const menuItems = primaryNav.map((n) => ({
    key: n.key,
    icon: n.icon,
    label: <Link to={n.key}>{t(n.label)}</Link>,
  }));

  // Highlight the parent nav item for drill-in routes like /app/mixers/:id.
  const selectedKey =
    [...primaryNav, ...MANAGER_NAV, ...SECONDARY_NAV].find((n) => location.pathname.startsWith(n.key))?.key ??
    location.pathname;

  return (
    <ConfigProvider
      direction={i18n.language === "he" ? "rtl" : "ltr"}
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorBgBase: "#121214",
          colorBgContainer: "#1b1b1e",
          colorBgLayout: "#121214",
          colorBorder: "#2a2a2e",
          colorBorderSecondary: "#2a2a2e",
          colorPrimary: "#3b82f6",
          fontFamily: "'IBM Plex Sans', 'Heebo', system-ui, -apple-system, sans-serif",
        },
      }}
    >
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={collapsed ? 72 : 220}
        className="desktop-sider"
        style={{ display: "flex", flexDirection: "column", background: "#18181b", borderRight: "1px solid #2a2a2e" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            color: "#f2f2f0",
            fontWeight: 700,
            fontSize: 15,
            padding: collapsed ? "16px 0" : "16px 10px 16px 16px",
            letterSpacing: "-0.3px",
            borderBottom: "1px solid #2a2a2e",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {!collapsed && <span>StrataOptimize</span>}
          <Tooltip title={collapsed ? "Expand" : "Collapse"} placement="right">
            <div
              onClick={() => setCollapsed((c) => !c)}
              style={{
                cursor: "pointer",
                color: "#9a9a97",
                display: "flex",
                alignItems: "center",
                padding: 6,
                borderRadius: 6,
              }}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </div>
          </Tooltip>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            items={menuItems}
            style={{ marginTop: 8, border: "none", background: "transparent" }}
          />
          {!collapsed && (
            <div style={{ padding: "10px 16px 4px", fontSize: 11, color: "#6b6a68", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              {t("appNav.managerSection")}
            </div>
          )}
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            items={MANAGER_NAV.map((n) => ({
              key: n.key,
              icon: n.icon,
              label: <Link to={n.key}>{t(n.label)}</Link>,
            }))}
            style={{ border: "none", background: "transparent" }}
          />
        </div>

        <div style={{ borderTop: "1px solid #2a2a2e" }}>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            items={SECONDARY_NAV.map((n) => ({
              key: n.key,
              icon: n.icon,
              label: <Link to={n.key}>{t(n.label)}</Link>,
            }))}
            style={{ border: "none", background: "transparent" }}
          />
          <SidebarUserMenu user={user} collapsed={collapsed} />
        </div>
      </Sider>

      <Layout>
        <Header
          className="desktop-topbar"
          style={{
            padding: 0,
            height: "auto",
            lineHeight: "normal",
            background: "transparent",
          }}
        >
          <TopBar user={user} />
        </Header>
        <Content className="app-content-area" style={{ background: "#121214" }}>
          <Suspense
            fallback={
              <Spin style={{ display: "block", margin: "80px auto" }} />
            }
          >
            <Outlet />
          </Suspense>
        </Content>
      </Layout>
      <MobileTabBar />
    </Layout>
    </ConfigProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <FactoryProvider>
        <RoleProvider>
        <BrowserRouter>
          <Suspense
            fallback={
              <Spin style={{ display: "block", margin: "80px auto" }} />
            }
          >
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/why" element={<Why />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/company/about" element={<CompanyAbout />} />
              <Route path="/company/press" element={<CompanyPress />} />
              <Route path="/app/login" element={<Login />} />
              <Route path="/app" element={<FleetLayout />}>
                <Route index element={<Navigate to="home" replace />} />
                <Route
                  path="home"
                  element={
                    <RequireAuth>
                      <Home />
                    </RequireAuth>
                  }
                />
                <Route
                  path="dashboards"
                  element={
                    <RequireAuth>
                      <Dashboards />
                    </RequireAuth>
                  }
                />
                <Route
                  path="map"
                  element={
                    <RequireAuth>
                      <TruckMap />
                    </RequireAuth>
                  }
                />
                <Route
                  path="tickets"
                  element={
                    <RequireAuth>
                      <Tickets />
                    </RequireAuth>
                  }
                />
                <Route
                  path="flow-commands"
                  element={
                    <RequireAuth>
                      <FlowCommandHistory />
                    </RequireAuth>
                  }
                />
                <Route
                  path="mixers"
                  element={
                    <RequireAuth>
                      <Mixers />
                    </RequireAuth>
                  }
                />
                <Route
                  path="mixers/:sensorId"
                  element={
                    <RequireAuth>
                      <MixerDetail />
                    </RequireAuth>
                  }
                />
                <Route
                  path="recipes"
                  element={
                    <RequireAuth>
                      <Recipes />
                    </RequireAuth>
                  }
                />
                <Route
                  path="mixer-status"
                  element={
                    <RequireAuth>
                      <MixerStatus />
                    </RequireAuth>
                  }
                />
                <Route
                  path="base-mixes"
                  element={
                    <RequireAuth>
                      <BaseMixes />
                    </RequireAuth>
                  }
                />
                <Route
                  path="realtime-corrections"
                  element={
                    <RequireAuth>
                      <RealtimeCorrections />
                    </RequireAuth>
                  }
                />
                <Route
                  path="field-additive"
                  element={
                    <RequireAuth>
                      <FieldAdditive />
                    </RequireAuth>
                  }
                />
                <Route
                  path="manager/overview"
                  element={
                    <RequireAuth>
                      <ManagerOverview />
                    </RequireAuth>
                  }
                />
                <Route
                  path="manager/driver-times"
                  element={
                    <RequireAuth>
                      <DriverTimes />
                    </RequireAuth>
                  }
                />
                <Route
                  path="manager/sites"
                  element={
                    <RequireAuth>
                      <ManagerSites />
                    </RequireAuth>
                  }
                />
                <Route
                  path="manager/stations"
                  element={
                    <RequireAuth>
                      <ManagerStations />
                    </RequireAuth>
                  }
                />
                <Route
                  path="manager/materials"
                  element={
                    <RequireAuth>
                      <ManagerMaterials />
                    </RequireAuth>
                  }
                />
                <Route
                  path="strength"
                  element={
                    <RequireAuth>
                      <Strength />
                    </RequireAuth>
                  }
                />
                <Route
                  path="pilot-summary"
                  element={
                    <RequireAuth>
                      <PilotSummary />
                    </RequireAuth>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <RequireAuth>
                      <Settings />
                    </RequireAuth>
                  }
                />
                <Route
                  path="support"
                  element={
                    <RequireAuth>
                      <Support />
                    </RequireAuth>
                  }
                />
                <Route
                  path="history"
                  element={
                    <RequireAuth>
                      <DeliveryHistory />
                    </RequireAuth>
                  }
                />
                <Route
                  path="savings"
                  element={
                    <RequireAuth>
                      <Savings />
                    </RequireAuth>
                  }
                />
                <Route
                  path="recommendations"
                  element={
                    <RequireAuth>
                      <Recommendations />
                    </RequireAuth>
                  }
                />
                <Route
                  path="chat"
                  element={
                    <RequireAuth>
                      <Chat />
                    </RequireAuth>
                  }
                />
                <Route
                  path="debug"
                  element={
                    <RequireAuth>
                      <Debug />
                    </RequireAuth>
                  }
                />
                <Route
                  path="pilot"
                  element={
                    <RequireAuth>
                      <PilotRunner />
                    </RequireAuth>
                  }
                />
                <Route path="*" element={<Navigate to="dashboards" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        </RoleProvider>
      </FactoryProvider>
    </QueryClientProvider>
  );
}
